/**
 * Utility to retrieve CSRF token from cookies.
 */
export function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrf_access_token=([^;]*)/);
  if (match) return match[1];
  return null;
}

export function getCsrfHeaders(): HeadersInit {
  const token = getCsrfToken();
  return token ? { 'x-csrf-token': token } : {};
}

export function getCsrfAdminToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrf_admin_token=([^;]*)/);
  if (match) return match[1];
  return null;
}

export function getCsrfAdminHeaders(): HeadersInit {
  const token = getCsrfAdminToken();
  return token ? { 'x-csrf-token': token } : {};
}

type CsrfErrorBody = {
  error?: { code?: string };
  detail?: string;
};

export async function isCsrfErrorResponse(response: Response): Promise<boolean> {
  if (response.status !== 403) return false;
  const body = await response.clone().json().catch(() => null) as CsrfErrorBody | null;
  return body?.error?.code === 'CSRF_ERROR';
}

/**
 * Returns a fetch wrapper that automatically retries once after refreshing
 * the CSRF cookie when the server responds with a CSRF_ERROR.
 *
 * @param csrfRefreshUrl - URL to GET in order to refresh the CSRF cookie.
 */
export function createFetchWithCsrfRetry(
  csrfRefreshUrl: string,
): (makeRequest: () => Promise<Response>) => Promise<Response> {
  let activeRefreshPromise: Promise<Response> | null = null;

  return async function fetchWithCsrfRetry(makeRequest: () => Promise<Response>): Promise<Response> {
    const response = await makeRequest();
    if (!(await isCsrfErrorResponse(response))) return response;

    if (!activeRefreshPromise) {
      activeRefreshPromise = fetch(csrfRefreshUrl, { cache: 'no-store' })
        .then(res => res.text()) // ensure body is read so browser persists cookie
        .then(() => new Promise<Response>(resolve => setTimeout(() => resolve(new Response(null)), 50))) // return dummy response
        .finally(() => {
          activeRefreshPromise = null;
        });
    }
    await activeRefreshPromise;
    return makeRequest();
  };
}
